import ApiResponseV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/ApiResponseV2";
import HttpClientV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/HttpClientV2";
import { DataRoomCustomFolder } from "@/models/data-room/DataRoomCustomFolder";
import { DataRoomFile } from "@/models/data-room/DataRoomFile";

export enum DATA_ROOM_CUSTOM_FOLDER_EVENTS {
  MOVED_FILES = "movedFiles",
  UPLOADED_FILES = "uploadedFiles",
  DELETED_FILES = "deletedFiles",
  DELETED_FOLDER = "deletedFolder",
  CREATED_FOLDER = "createdFolder",
}

export type DataRoomCustomMovedFilesEvent = {
  count: number;
  toExternalId?: string;
  fromExternalId?: string;
};

export type DataRoomCustomUploadedFilesEvent = {
  count: number;
  folderExternalId: string;
};

export type DataRoomCustomCreatedFolderEvent = {
  parentExternalId?: string;
  folder: DataRoomCustomFolder;
};

export type DataRoomCustomDeletedFolderEvent = {
  folder: DataRoomCustomFolder;
};

export type DataRoomCustomDeletedFilesEvent = {
  count: number;
  fromExternalId: string;
};

class DataRoomCustomFolderService {
  private path: string;
  constructor(p: string) {
    this.path = p;
  }

  get(tenantExternalId: string, parentExternalId?: string): Promise<ApiResponseV2<DataRoomCustomFolder[]>> {
    let qry = `?tenantExternalId=${tenantExternalId}`;
    if (parentExternalId) {
      qry += `&parentExternalId=${parentExternalId}`;
    }
    return HttpClientV2.get(`${this.path}${qry}`);
  }

  create(name: string, tenantExternalId: string, parentExternalId?: string): Promise<ApiResponseV2<DataRoomCustomFolder>> {
    return new Promise<ApiResponseV2<DataRoomCustomFolder>>((res, rej) => {
      HttpClientV2.post<any, DataRoomCustomFolder>(`${this.path}/create`, {
        name: name,
        tenantExternalId: tenantExternalId,
        parentExternalId: parentExternalId ? parentExternalId : null,
      })
        .then((r) => {
          r.onSuccess((c) => {
            const eventPayload: DataRoomCustomCreatedFolderEvent = {
              folder: c,
              parentExternalId: parentExternalId,
            };

            document.dispatchEvent(new CustomEvent(DATA_ROOM_CUSTOM_FOLDER_EVENTS.CREATED_FOLDER, { detail: eventPayload }));
          });

          res(r);
        })
        .catch(rej);
    });
  }

  delete(folder: DataRoomCustomFolder): Promise<ApiResponseV2<string>> {
    return new Promise<ApiResponseV2<string>>((res, rej) => {
      HttpClientV2.post<any, string>(`${this.path}/delete`, {
        externalIds: [folder.externalId],
      })
        .then((r) => {
          const eventPayload: DataRoomCustomDeletedFolderEvent = {
            folder: folder,
          };

          const ev = new CustomEvent(DATA_ROOM_CUSTOM_FOLDER_EVENTS.DELETED_FOLDER, { detail: eventPayload });
          document.dispatchEvent(ev);

          res(r);
        })
        .catch(rej);
    });
  }

  getFiles(externalId: string): Promise<ApiResponseV2<DataRoomFile[]>> {
    return HttpClientV2.get(`${this.path}/files?externalId=${externalId}`);
  }

  uploadFiles(files: File[], tenantExternalId: string, parentId: string): Promise<ApiResponseV2<DataRoomFile[]>> {
    const qryString = `?tenantExternalId=${tenantExternalId}&customParentId=${parentId}`;
    const formData = new FormData();
    files.forEach((f) => {
      formData.append("files", f);
    });

    return new Promise<ApiResponseV2<DataRoomFile[]>>((res, rej) =>
      HttpClientV2.post<any, DataRoomFile[]>(`${this.path}/files${qryString}`, formData, {
        axiosConfig: {
          timeout: 60000,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      })
        .then((r) => {
          const eventPayload: DataRoomCustomUploadedFilesEvent = {
            count: files.length,
            folderExternalId: parentId,
          };

          document.dispatchEvent(new CustomEvent(DATA_ROOM_CUSTOM_FOLDER_EVENTS.UPLOADED_FILES, { detail: eventPayload }));

          res(r);
        })
        .catch(rej)
    );
  }

  getTree(tenantExternalId: string): Promise<ApiResponseV2<DataRoomCustomFolder[]>> {
    return HttpClientV2.get(`${this.path}/tree?tenantExternalId=${tenantExternalId}`);
  }

  moveFiles(fileExternalIds: string[], toExternalId: string, fromExternalId?: string): Promise<ApiResponseV2<string>> {
    return new Promise<ApiResponseV2<string>>((res, rej) =>
      HttpClientV2.post<any, string>(`${this.path}/move/files`, {
        fileExternalIds,
        toExternalId,
      })
        .then((r) => {
          const eventPayload: DataRoomCustomMovedFilesEvent = {
            count: fileExternalIds.length,
            toExternalId: toExternalId,
            fromExternalId: fromExternalId,
          };

          document.dispatchEvent(
            new CustomEvent(DATA_ROOM_CUSTOM_FOLDER_EVENTS.MOVED_FILES, {
              detail: eventPayload,
            })
          );

          res(r);
        })
        .catch(rej)
    );
  }
}

export default new DataRoomCustomFolderService("/api/data-room/custom/folder");
